import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { 
  SubscriptionTier, 
  SubscriptionLimits, 
  SubscriptionUsage, 
  FeatureSet, 
  SubscriptionContextType
} from '../types/subscription';
import { apiFetch } from '../lib/api';
import { normalizeSubscriptionTier } from '../utils/subscriptionTierNormalize';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

interface SubscriptionProviderProps {
  children: ReactNode;
}

// Phase 2: aligned fallback defaults. Real values come from API.
const DEFAULT_LIMITS: Record<SubscriptionTier, SubscriptionLimits> = {
  starter: {
    users: 6,
    agents: 5,
    storage: 0.5, // 500MB
    apiCalls: 0,
    customDashboards: 1,
    exports: 10,
    support: 'email',
    retention: 90,
    integrations: 1,
    customRoles: 1,
  },
  up: {
    users: 15,
    agents: 25,
    storage: 2, // 2GB
    apiCalls: 1000,
    customDashboards: 10,
    exports: 1000,
    support: 'email',
    retention: 365,
    integrations: 2,
    customRoles: 5,
  },
  scale: {
    users: -1, // unlimited
    agents: -1, // unlimited
    storage: -1, // custom
    apiCalls: -1, // custom
    customDashboards: -1, // unlimited
    exports: -1, // unlimited
    support: 'dedicated',
    retention: -1, // unlimited
    integrations: -1, // all
    customRoles: -1, // unlimited
  },
  a_la_carte: {
    users: 1,
    agents: 1,
    storage: 0,
    apiCalls: 0,
    customDashboards: 0,
    exports: 0,
    support: 'community',
    retention: 30,
    integrations: 0,
    customRoles: 0,
  },
};

const DEFAULT_FEATURES: Record<SubscriptionTier, FeatureSet> = {
  starter: {
    advancedAnalytics: false,
    customBranding: false,
    ssoIntegration: false,
    auditLogs: false,
    prioritySupport: false,
    customIntegrations: false,
    advancedSecurity: false,
    dedicatedInfrastructure: false,
    professionalServices: false,
  },
  up: {
    advancedAnalytics: true,
    customBranding: true,
    ssoIntegration: false,
    auditLogs: true,
    prioritySupport: true,
    customIntegrations: true,
    advancedSecurity: false,
    dedicatedInfrastructure: false,
    professionalServices: false,
  },
  scale: {
    advancedAnalytics: true,
    customBranding: true,
    ssoIntegration: true,
    auditLogs: true,
    prioritySupport: true,
    customIntegrations: true,
    advancedSecurity: true,
    dedicatedInfrastructure: true,
    professionalServices: true,
  },
  a_la_carte: {
    advancedAnalytics: false,
    customBranding: false,
    ssoIntegration: false,
    auditLogs: false,
    prioritySupport: false,
    customIntegrations: false,
    advancedSecurity: false,
    dedicatedInfrastructure: false,
    professionalServices: false,
  },
};

const mapSupportTier = (slaHours: number | undefined): SubscriptionLimits['support'] => {
  if (slaHours === undefined || slaHours === null) return 'community';
  if (slaHours <= 4) return 'dedicated';
  if (slaHours <= 24) return 'priority';
  if (slaHours <= 48) return 'email';
  return 'community';
};

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState<{
    tier: SubscriptionTier;
    limits: SubscriptionLimits;
    usage: SubscriptionUsage;
    features: FeatureSet;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSubscription = useCallback(async () => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setSubscriptionData(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const [subscriptionResponse, entitlementsResponse, orgStatsResponse] = await Promise.all([
        apiFetch('/api/v1/subscription').catch(() => null),
        apiFetch('/api/v1/subscription/entitlements').catch(() => null),
        apiFetch('/api/v1/organizations/me/stats').catch(() => null),
      ]);

      const tier = normalizeSubscriptionTier(
        subscriptionResponse?.tier,
        subscriptionData?.tier ?? 'a_la_carte',
      );
      const entitlements = entitlementsResponse?.entitlements || {};
      const defaultLimits = DEFAULT_LIMITS[tier];

      const resolvedLimits: SubscriptionLimits = {
        ...defaultLimits,
        users: entitlements.max_users ?? defaultLimits.users,
        agents: entitlements.max_partners ?? defaultLimits.agents,
        storage: entitlements.knowledge_base_mb
          ? Math.round((Number(entitlements.knowledge_base_mb) / 1024) * 100) / 100
          : defaultLimits.storage,
        apiCalls: entitlements.api_calls_per_month ?? defaultLimits.apiCalls,
        integrations: entitlements.marketplace_connectors ?? defaultLimits.integrations,
        support: mapSupportTier(entitlements.support_sla_hours),
      };

      const resolvedUsage: SubscriptionUsage = {
        users: Number(orgStatsResponse?.total_users || 0),
        agents: Number(orgStatsResponse?.active_projects || 0),
        storage: 0,
        apiCalls: 0,
        customDashboards: 0,
        exports: 0,
        integrations: 0,
        customRoles: 0,
        lastUpdated: new Date(),
      };

      const resolvedFeatures: FeatureSet = {
        ...DEFAULT_FEATURES[tier],
        ssoIntegration: Boolean(entitlements.sso_enabled),
        customIntegrations: Boolean(entitlements.api_access_enabled),
        auditLogs: tier === 'up' || tier === 'scale',
        advancedAnalytics: tier === 'up' || tier === 'scale',
        prioritySupport: Number(entitlements.support_sla_hours || 999) <= 24,
      };

      setSubscriptionData({
        tier,
        limits: resolvedLimits,
        usage: resolvedUsage,
        features: resolvedFeatures,
      });
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      // Preserve last known state; if none exists, use conservative fallback.
      setSubscriptionData((previous) => previous ?? {
        tier: 'a_la_carte',
        limits: DEFAULT_LIMITS.a_la_carte,
        usage: {
          users: 0,
          agents: 0,
          storage: 0,
          apiCalls: 0,
          customDashboards: 0,
          exports: 0,
          integrations: 0,
          customRoles: 0,
          lastUpdated: new Date(),
        },
        features: DEFAULT_FEATURES.a_la_carte,
      });
    } finally {
      setIsLoading(false);
    }
  }, [authLoading, isAuthenticated, subscriptionData?.tier]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setSubscriptionData(null);
      setIsLoading(false);
      return;
    }

    refreshSubscription();
    const refreshInterval = window.setInterval(() => {
      refreshSubscription();
    }, 60000);

    return () => window.clearInterval(refreshInterval);
  }, [authLoading, isAuthenticated, refreshSubscription]);

  const checkFeatureAccess = useCallback((feature: string): boolean => {
    if (!subscriptionData) return false;
    return subscriptionData.features[feature as keyof FeatureSet] || false;
  }, [subscriptionData]);

  const getUsagePercentage = useCallback((metric: string): number => {
    if (!subscriptionData) return 0;
    const used = subscriptionData.usage[metric as keyof SubscriptionUsage] as number;
    const limit = subscriptionData.limits[metric as keyof SubscriptionLimits] as number;
    
    // Handle unlimited (-1) limits
    if (limit === -1) return 0;
    
    return limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  }, [subscriptionData]);

  const isNearLimit = useCallback((metric: string, threshold = 80): boolean => {
    const percentage = getUsagePercentage(metric);
    return percentage >= threshold;
  }, [getUsagePercentage]);

  const checkUserLimit = useCallback((): boolean => {
    if (!subscriptionData) return true;
    const limit = subscriptionData.limits.users;
    if (limit === -1) return true; // unlimited
    const used = subscriptionData.usage.users as number;
    return used < limit;
  }, [subscriptionData]);

  const value = useMemo(() => ({
    tier: subscriptionData?.tier || 'a_la_carte',
    limits: subscriptionData?.limits || DEFAULT_LIMITS.a_la_carte,
    usage: subscriptionData?.usage || {
      users: 0,
      agents: 0,
      storage: 0,
      apiCalls: 0,
      customDashboards: 0,
      exports: 0,
      integrations: 0,
      customRoles: 0,
      lastUpdated: new Date(),
    },
    features: subscriptionData?.features || DEFAULT_FEATURES.a_la_carte,
    isLoading,
    refreshSubscription,
    checkFeatureAccess,
    checkUserLimit,
    getUsagePercentage,
    isNearLimit,
  }), [subscriptionData, isLoading, refreshSubscription, checkFeatureAccess, checkUserLimit, getUsagePercentage, isNearLimit]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
